import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FOCUS_RING_ONLY } from './FieldShell';
import { useAppDispatch } from '../../../store';
import { showModal } from '../../../store/actions/modalActions';
import { PiCloudArrowUpLight, PiVaultLight, PiFilePlusLight, PiPaperclipLight, PiTextAlignLeftLight, PiArticleLight, PiFolderLight } from "react-icons/pi";
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
// Google Drive and OneDrive pickers disabled until OAuth integration is complete.
// import GoogleDrivePickerButton from '../../../Globals/GoogleDrivePickerButton';
// import OneDrivePickerButton from '../../../Globals/OneDrivePickerButton';
import Button from './Button';
import { Tooltip } from '@mui/material';
import { validateFileUpload } from '../../../utils/validation/validators';
import { validateFileSecure, sanitizeFileName } from '../../../utils/security/fileSecurityValidator';
import { MAX_UPLOAD_BYTES } from '../../../utils/uploadLimits';

// Whitelist of allowed file extensions - DOCUMENTS ONLY
// Images and media uploads are NOT allowed
const DOCS_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'msg', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'];

// Default to documents only
const ALLOWED_FILE_EXTENSIONS = DOCS_EXTENSIONS;

// Maximum individual file size — the one app-wide cap (matches the backend).
const MAX_FILE_SIZE_BYTES = MAX_UPLOAD_BYTES;

interface FileUploaderProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  allowedExtensions?: string[];
  maxSizeBytes?: number;
  maxFiles?: number;
}

const getFileIcon = (fileType: string) => {
  if (!fileType) return <PiFilePlusLight className="h-5 w-5 text-gray-500" />;
  if (fileType.includes('pdf')) return <PiFilePlusLight className="h-5 w-5 text-danger-500" />;
  if (fileType.includes('word') || fileType.includes('msword')) return <PiFilePlusLight className="h-5 w-5 text-info-500" />;
  if (fileType.includes('image')) return <PiFilePlusLight className="h-5 w-5 text-success-500" />;
  if (fileType.includes('text')) return <PiFilePlusLight className="h-5 w-5 text-gray-500" />;
  if (fileType.includes('zip')) return <PiFilePlusLight className="h-5 w-5 text-warning-500" />;
  return <PiFilePlusLight className="h-5 w-5 text-gray-500" />;
};

const FileUploader: React.FC<FileUploaderProps> = ({
  files,
  onFilesChange,
  allowedExtensions: propExtensions,
  maxSizeBytes = MAX_FILE_SIZE_BYTES,
  maxFiles,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Use provided extensions or default to documents only
  const allowedExtensions = propExtensions || DOCS_EXTENSIONS;

  // Generate accept attribute for file input
  const acceptAttr = allowedExtensions.map(ext => `.${ext}`).join(',');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const menuButtonRef1 = useRef<HTMLButtonElement | null>(null);
  const menuButtonRef2 = useRef<HTMLButtonElement | null>(null);
  const menuListRef1 = useRef<HTMLDivElement | null>(null);
  const menuListRef2 = useRef<HTMLDivElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [fileError, setFileError] = useState<string>();
  const [menuPosition1, setMenuPosition1] = useState({ top: 0, left: 0 });
  const [menuPosition2, setMenuPosition2] = useState({ top: 0, left: 0 });
  const [isMenuOpen1, setIsMenuOpen1] = useState(false);
  const [isMenuOpen2, setIsMenuOpen2] = useState(false);
  const [shouldDropUp1, setShouldDropUp1] = useState(false);
  const [shouldDropUp2, setShouldDropUp2] = useState(false);

  // Refs to prevent stale closures in async operations (e.g. validateFileSecure)
  // Set directly during render (not via useEffect) to eliminate any timing gap
  const filesRef = useRef<File[]>(files);
  filesRef.current = files;
  const onFilesChangeRef = useRef(onFilesChange);
  onFilesChangeRef.current = onFilesChange;

  const validateAndAddFiles = async (newFiles: File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    // Enforce maxFiles limit
    if (maxFiles) {
      const remaining = maxFiles - filesRef.current.length;
      if (remaining <= 0) {
        setFileError(t('Maximum of {{count}} file(s) allowed', { count: maxFiles }));
        return;
      }
      if (newFiles.length > remaining) {
        newFiles = newFiles.slice(0, remaining);
        errors.push(t('Maximum of {{count}} file(s) allowed — extra files were ignored', { count: maxFiles }));
      }
    }

    // TEMPORARY: Check if file security validator is disabled for backend testing
    const skipSecurityCheck = process.env.REACT_APP_SKIP_FILE_SECURITY_CHECK === 'true';
    const isProduction = process.env.NODE_ENV === 'production';

    // Security check: Prevent bypass flag in production
    if (skipSecurityCheck && isProduction) {
      console.error('[FileUploader] CRITICAL: File security bypass is ENABLED in production! This is a security risk.');
      throw new Error('Security bypass cannot be enabled in production environment');
    }

    if (skipSecurityCheck) {
      console.warn('[FileUploader] WARNING: File security checker is DISABLED for backend testing. This should ONLY be enabled in development/staging!');
    }

    for (const file of newFiles) {
      // First check basic validation
      const basicValidation = validateFileUpload(file, maxSizeBytes, allowedExtensions);

      if (!basicValidation.isValid) {
        errors.push(`${file.name}: ${basicValidation.error}`);
        continue;
      }

      // Then check security validation (MIME type and filename) - can be skipped for backend testing
      if (!skipSecurityCheck) {
        const securityValidation = await validateFileSecure(file, allowedExtensions, maxSizeBytes);

        if (!securityValidation.isValid) {
          errors.push(`${file.name}: ${securityValidation.errors.join('; ')}`);
          continue;
        }

        // Use processed file (with metadata stripped) if available
        const fileToAdd = securityValidation.processedFile || file;
        validFiles.push(fileToAdd);
      } else {
        validFiles.push(file);
      }
    }

    // Display error messages if any files were rejected
    if (errors.length > 0) {
      setFileError(errors.join(', '));
    } else {
      setFileError(undefined);
    }

    // Add only valid files — use refs for the latest state after async gaps
    if (validFiles.length > 0) {
      onFilesChangeRef.current([...filesRef.current, ...validFiles]);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      await validateAndAddFiles(newFiles);
    }
  };

  const handleFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      await validateAndAddFiles(newFiles);
    }
  };

  // Set folder picker attributes on the input element
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('mozdirectory', '');
    }
  }, []);

  const updateMenuPosition = useCallback((
    buttonRef: React.RefObject<HTMLButtonElement | null>,
    menuRef: React.RefObject<HTMLDivElement | null>,
    setPosition: (pos: { top: number; left: number }) => void,
    setShouldUp: (shouldUp: boolean) => void
  ) => {
    if (buttonRef.current && menuRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current.offsetHeight;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - rect.bottom;

      const shouldFlipUp = spaceBelow < menuHeight && rect.top > menuHeight;
      setShouldUp(shouldFlipUp);

      setPosition({
        top: shouldFlipUp
          ? rect.top - menuHeight + window.scrollY
          : rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  }, []);

  // Update position for menu 1 when it opens
  useEffect(() => {
    if (isMenuOpen1) {
      updateMenuPosition(menuButtonRef1, menuListRef1, setMenuPosition1, setShouldDropUp1);
      const handleScroll = () => updateMenuPosition(menuButtonRef1, menuListRef1, setMenuPosition1, setShouldDropUp1);
      const handleResize = () => updateMenuPosition(menuButtonRef1, menuListRef1, setMenuPosition1, setShouldDropUp1);

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isMenuOpen1, updateMenuPosition]);

  // Update position for menu 2 when it opens
  useEffect(() => {
    if (isMenuOpen2) {
      updateMenuPosition(menuButtonRef2, menuListRef2, setMenuPosition2, setShouldDropUp2);
      const handleScroll = () => updateMenuPosition(menuButtonRef2, menuListRef2, setMenuPosition2, setShouldDropUp2);
      const handleResize = () => updateMenuPosition(menuButtonRef2, menuListRef2, setMenuPosition2, setShouldDropUp2);

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isMenuOpen2, updateMenuPosition]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const triggerEl1 = menuButtonRef1.current;
      const menuEl1 = menuListRef1.current;
      const triggerEl2 = menuButtonRef2.current;
      const menuEl2 = menuListRef2.current;

      if (
        isMenuOpen1 &&
        triggerEl1 &&
        !triggerEl1.contains(target) &&
        menuEl1 &&
        !menuEl1.contains(target)
      ) {
        setIsMenuOpen1(false);
      }

      if (
        isMenuOpen2 &&
        triggerEl2 &&
        !triggerEl2.contains(target) &&
        menuEl2 &&
        !menuEl2.contains(target)
      ) {
        setIsMenuOpen2(false);
      }
    };

    if (isMenuOpen1 || isMenuOpen2) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen1, isMenuOpen2]);

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const extractFilesFromEntries = async (entries: any[], parentPath: string = ''): Promise<File[]> => {
    const files: File[] = [];

    for (const entry of entries) {
      if (entry.isFile) {
        /*
          Handle file entries — the ORIGINAL File is what gets pushed.

          This used to re-wrap every dropped file as `new File([f], f.name, …)`
          purely to hang a `webkitRelativePath` on it, and the re-wrap lost the
          bytes: validation then saw `size === 0` and rejected the file with
          "File is empty", while the same file imported fine through the
          picker. The path is attached to the original object instead, so the
          blob behind it is never touched.
        */
        const file = await new Promise<File | null>((resolve) => {
          entry.file(
            (f: File) => {
              const relativePath = parentPath ? `${parentPath}/${f.name}` : f.name;
              try {
                Object.defineProperty(f, 'webkitRelativePath', {
                  value: relativePath,
                  writable: false,
                  configurable: true,
                });
              } catch {
                // Some browsers refuse to redefine the property. The file
                // itself still uploads; only the folder path is lost.
              }
              resolve(f);
            },
            // Without this the promise never settles on a read error and the
            // whole drop hangs.
            (err: unknown) => {
              console.error('Failed to read dropped file entry:', entry?.name, err);
              resolve(null);
            },
          );
        });
        if (file) files.push(file);
      } else if (entry.isDirectory) {
        // Recursively handle directory entries — track the path
        const dirPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
        const reader = entry.createReader();
        // readEntries() returns at most ~100 entries per call — it MUST be
        // called repeatedly until it returns an empty array, otherwise folders
        // with more than ~100 files are silently truncated (#2234: dropping
        // the next 300 docs only picked up the first 100).
        const subEntries: any[] = [];
        let batch: any[];
        do {
          // eslint-disable-next-line no-await-in-loop -- reader is stateful; calls must be sequential
          batch = await new Promise<any[]>((resolve) => {
            reader.readEntries(
              (entries: any[]) => resolve(entries),
              () => resolve([]),
            );
          });
          subEntries.push(...batch);
        } while (batch.length > 0);
        const subFiles = await extractFilesFromEntries(subEntries, dirPath);
        files.push(...subFiles);
      }
    }

    return files;
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    // Capture everything from the drop event SYNCHRONOUSLY. The DataTransfer
    // (items + files) is invalidated as soon as this handler yields to the
    // event loop (the first await), so reading it afterwards returns nothing —
    // a big driver of the "drag-and-drop unreliable" behaviour (#2234). We grab
    // the entries and a plain-file fallback up front, before any await.
    const entries: any[] = [];
    const items = e.dataTransfer.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const entry = (item as any).webkitGetAsEntry?.();
          if (entry) entries.push(entry);
        }
      }
    }
    const fileListFallback: File[] = e.dataTransfer.files
      ? Array.from(e.dataTransfer.files)
      : [];

    try {
      let droppedFiles: File[] = [];

      // Preferred path: entries API (supports folders + recursion).
      if (entries.length > 0) {
        droppedFiles = await extractFilesFromEntries(entries);
      }

      // Fallback: browsers without the entries API, or entry extraction yielded
      // nothing. Uses the synchronously-captured file list, so plain multi-file
      // drops always work regardless of the items/entries support above.
      if (droppedFiles.length === 0 && fileListFallback.length > 0) {
        droppedFiles = fileListFallback;
      }

      if (droppedFiles.length > 0) {
        await validateAndAddFiles(droppedFiles);
      }
    } catch (error) {
      console.error('Error processing dropped files:', error);
      setFileError(t('Error processing dropped files. Please try again.'));
    }
  };

  const handleOpenFileDialog = (e: any) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleOpenFolderDialog = (e: any) => {
    e.stopPropagation();
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
      folderInputRef.current.click();
    }
  };

  const handleImportFromVault = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(
      showModal({
        type: 'vaultFilePicker',
        onConfirm: () => {},
        showModal: true,
        message: '',
        onFilesChange: onFilesChange,
        files: files,
      })
    );
  };

  const handleAddTextContent = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(
      showModal({
        type: 'addTextContent',
        message: t('Add Text Content'),
        icon: <PiArticleLight className='w-6 h-6 text-gray-600' />,
        subMsg: t('Write the content you want to upload.'),
        onConfirm: async (textContent: string) => {
          if (textContent.trim()) {
            const blob = new Blob([textContent], { type: 'text/plain' });
            const sanitizedFilename = sanitizeFileName(`text_content_${Date.now()}.txt`);
            const txtFile = new File([blob], sanitizedFilename, { type: 'text/plain' });
            // Validate the text file before adding
            await validateAndAddFiles([txtFile]);
          }
        },
        showModal: true,
      })
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const fileVariants = {
    hidden: { opacity: 0, x: -30, rotate: -10 },
    visible: { opacity: 1, x: 0, rotate: 0, transition: { type: 'spring', stiffness: 300, damping: 20 } },
    exit: { opacity: 0, x: 30, rotate: 10 },
  } as const;

  const displayedFiles = files.slice(0, 8);
  const moreFilesCount = files.length - 8;

  return (
    <div className="relative overflow-visible">
      <div
        className={`border-2 border-dashed border-hairline rounded-lg p-6 transition-all duration-300 hover:bg-surface-sunken ${
          isDragOver ? 'bg-champagne/5 border-champagne/60' : 'bg-surface-sunken/70'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="region"
        aria-label={t('File upload area')}
      >
        {files.length === 0 ? (
          <div className="flex flex-col items-center text-center h-[150px]">
            <PiCloudArrowUpLight className="h-10 w-10 text-fg-muted mb-3" />
            <p className="text-sm text-fg-body mb-2">
              {t('Import files or drag & drop them here')}
            </p>
            <p className="text-xs text-fg-muted mb-4">
              {t('Allowed types')}: {allowedExtensions.slice(0, 5).join(', ')}
              {allowedExtensions.length > 5 ? '...' : ''}
            </p>
            {fileError && (
              <p
                role="alert"
                aria-live="polite"
                aria-atomic="true"
                className="text-xs text-danger-500 mb-2"
              >
                {fileError}
              </p>
            )}
            <div className="inline-block text-left">
              <button
                type="button"
                onClick={() => setIsMenuOpen1(!isMenuOpen1)}
                ref={menuButtonRef1}
                className="rounded bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 inline-flex items-center"
                aria-label={t('Open upload menu')}
                aria-expanded={isMenuOpen1}
                aria-haspopup="menu"
              >
                <PiPaperclipLight className="h-5 w-5" />
              </button>
              {isMenuOpen1 && createPortal(
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className={`fixed w-48 glass rounded-md shadow-lg focus:outline-none z-[300000001] ${shouldDropUp1 ? 'origin-bottom-left' : 'origin-top-left'}`}
                  style={{
                    top: `${menuPosition1.top}px`,
                    left: `${menuPosition1.left}px`,
                  }}
                  ref={menuListRef1}
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        handleOpenFileDialog(e);
                        setIsMenuOpen1(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-fg-body hover:bg-surface-soft transition-colors"
                    >
                      <PiCloudArrowUpLight className="mr-2" />
                      {t('Upload File')}
                    </button>
                    <button
                      onClick={(e) => {
                        handleOpenFolderDialog(e);
                        setIsMenuOpen1(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-fg-body hover:bg-surface-soft transition-colors"
                    >
                      <PiFolderLight className="mr-2" />
                      {t('Upload Folder')}
                    </button>
                    <button
                      onClick={(e) => {
                        handleAddTextContent(e);
                        setIsMenuOpen1(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-fg-body hover:bg-surface-soft transition-colors"
                    >
                      <PiTextAlignLeftLight className="mr-2" />
                      {t('Add Text Content')}
                    </button>
                    {/* Google Drive and OneDrive pickers hidden — OAuth integration
                        not fully completed. Restore once credentials are wired up. */}
                    {/* <div onClick={() => setIsMenuOpen1(false)}>
                      <GoogleDrivePickerButton
                        onFilePicked={(file: File) => {
                          onFilesChange([...files, file]);
                        }}
                      />
                    </div>
                    <div onClick={() => setIsMenuOpen1(false)}>
                      <OneDrivePickerButton
                        onFileSelected={(file: File) => {
                          onFilesChange([...files, file]);
                        }}
                      />
                    </div> */}
                  </div>
                </motion.div>
              , document.body)}
            </div>
          </div>
        ) : (
          <>
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 h-[150px]"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence>
              {displayedFiles.map((file, index) => (
                <motion.div
                  key={file.name + index}
                  variants={fileVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="relative p-3 bg-surface-sunken/70 border border-hairline-soft rounded-md shadow-sm hover:shadow-md transition-shadow max-h-fit"
                >
                  <div className="flex items-center space-x-2">
                    {getFileIcon(file.type)}
                    <span className="text-sm text-fg-body truncate">{file.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="absolute top-1 right-1 text-fg-muted hover:text-danger-500 transition-colors"
                    aria-label={t('Remove file: ') + file.name}
                    title={t('Remove file')}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
            {moreFilesCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAllFiles(true)}
                className={`flex items-center justify-center p-3 bg-surface-sunken/70 rounded-md hover:bg-surface-sunken transition-colors ${FOCUS_RING_ONLY}`}
                aria-label={t('View all ') + moreFilesCount + t(' more files')}
              >
                <span className="text-sm text-fg-muted">+{moreFilesCount} more</span>
              </button>
            )}
            <div className="inline-block text-left">
              <button
                type="button"
                onClick={() => setIsMenuOpen2(!isMenuOpen2)}
                ref={menuButtonRef2}
                className={`rounded-md border border-hairline hover:bg-surface-sunken/70 px-3 py-2 inline-flex items-center max-h-fit ${FOCUS_RING_ONLY}`}
                aria-label={t('Add more files')}
                aria-expanded={isMenuOpen2}
                aria-haspopup="menu"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
              {isMenuOpen2 && createPortal(
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className={`fixed w-48 glass rounded-md shadow-lg focus:outline-none z-[300000001] ${shouldDropUp2 ? 'origin-bottom-left' : 'origin-top-left'}`}
                  style={{
                    top: `${menuPosition2.top}px`,
                    left: `${menuPosition2.left}px`,
                  }}
                  ref={menuListRef2}
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        handleOpenFileDialog(e);
                        setIsMenuOpen2(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-fg-body hover:bg-surface-soft transition-colors"
                    >
                      <PiCloudArrowUpLight className="mr-2" />
                      {t('Upload File')}
                    </button>
                    <button
                      onClick={(e) => {
                        handleOpenFolderDialog(e);
                        setIsMenuOpen2(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-fg-body hover:bg-surface-soft transition-colors"
                    >
                      <PiFolderLight className="mr-2" />
                      {t('Upload Folder')}
                    </button>
                    <button
                      onClick={(e) => {
                        handleAddTextContent(e);
                        setIsMenuOpen2(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-fg-body hover:bg-surface-soft transition-colors"
                    >
                      <PiTextAlignLeftLight className="mr-2" />
                      {t('Add Text Content')}
                    </button>
                    {/* Google Drive and OneDrive pickers hidden — OAuth integration
                        not fully completed. Restore once credentials are wired up. */}
                    {/* <div onClick={() => setIsMenuOpen2(false)}>
                      <GoogleDrivePickerButton
                        onFilePicked={(file: File[]) => {
                          onFilesChange([...files, ...file]);
                        }}
                      />
                    </div>
                    <div onClick={() => setIsMenuOpen2(false)}>
                      <OneDrivePickerButton
                        onFileSelected={(file: File[]) => {
                          onFilesChange([...files, ...file]);
                        }}
                      />
                    </div> */}
                  </div>
                </motion.div>
              , document.body)}
            </div>
          </motion.div>
          <div className="flex items-center justify-between mt-2">
            {fileError && (
              <p role="alert" aria-live="polite" aria-atomic="true" className="text-xs text-danger-500">
                {fileError}
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                onFilesChange([]);
                setFileError(undefined);
              }}
              className="ml-auto text-xs text-fg-muted hover:text-danger-500 transition-colors"
              aria-label={t('Clear all files')}
            >
              {t('Clear all')}
            </button>
          </div>
          </>
        )}
        <input
          type="file"
          ref={fileInputRef}
          multiple={!maxFiles || maxFiles > 1}
          className="hidden"
          onChange={handleFileChange}
          accept={acceptAttr}
          aria-label={t('Select files to upload')}
        />
        <input
          type="file"
          ref={folderInputRef}
          multiple={!maxFiles || maxFiles > 1}
          className="hidden"
          onChange={handleFolderChange}
          accept={acceptAttr}
          aria-label={t('Select folder to upload')}
        />
      {showAllFiles && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute top-0 left-0 w-full glass rounded-md shadow-lg z-10 p-4 mt-2"
          role="dialog"
          aria-modal="true"
          aria-labelledby="all-files-title"
        >
          <div className="flex justify-between items-center mb-3">
            <h3
              id="all-files-title"
              /* `text-md` is not a Tailwind size — it resolved to nothing, so
                 this was inherited-size semibold, heavier than every other
                 panel heading in the app. Panel headings are `text-sm`. */
              className="text-sm text-fg-strong"
            >
              {t('All Files')}
            </h3>
            <button
              type="button"
              onClick={() => setShowAllFiles(false)}
              className="text-fg-muted hover:text-fg-strong transition-colors"
              aria-label={t('Close file list')}
              title={t('Close')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2 custom-scrollbar-main">
            {files.map((file, index) => (
              <div
                key={file.name + index}
                className="flex items-center justify-between p-2 bg-surface-sunken/70 border border-hairline-soft rounded-md"
              >
                <div className="flex items-center space-x-2">
                  {getFileIcon(file.type)}
                  <span className="text-xs text-fg-body truncate">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className="text-fg-muted hover:text-danger-500 transition-colors"
                  aria-label={t('Remove file: ') + file.name}
                  title={t('Remove file')}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  </div>
  );
};

export default FileUploader;
